import { useCallback } from 'react';
import type { CalculationMode } from '@/lib/portfolioCalculations';

interface TotalsLike {
  totalDollarAmount: number;
  hasOverflow: boolean;
}

export function usePortfolioAutoSize(
  calcMode: CalculationMode,
  currentSize: number,
  updatePortfolioSize: (size: number) => Promise<boolean>,
  setOverrides: React.Dispatch<React.SetStateAction<Record<string, number>>>,
  computedTotals: TotalsLike,
  persistedTotalsDollar: TotalsLike,
  fallbackTotals: TotalsLike,
) {
  return useCallback(async (targetSize?: number) => {
    let requiredSize: number | undefined = targetSize;
    if (!Number.isFinite(requiredSize as number) || (requiredSize as number) <= 0) {
      if (calcMode === 'dollar') {
        requiredSize = computedTotals.hasOverflow
          ? computedTotals.totalDollarAmount
          : persistedTotalsDollar.totalDollarAmount;
      } else {
        requiredSize = fallbackTotals.totalDollarAmount;
      }
    }

    if (import.meta.env.DEV) console.debug('handleAutoSize called:', { targetSize, resolved: requiredSize, mode: calcMode });

    const current = Number(currentSize) || 0;
    if (Number.isFinite(requiredSize as number) && Math.abs((requiredSize as number) - current) < 0.01) {
      if (import.meta.env.DEV) console.debug('handleAutoSize skipped: target equals current size', { current, requiredSize });
      return;
    }

    try {
      const raw = sessionStorage.getItem('portfolio-pending-oversize');
      if (raw) {
        const pending = JSON.parse(raw || '{}');
        const { allocationId, dollarValue, expires } = pending || {};
        if (allocationId && Number.isFinite(Number(dollarValue)) && (!expires || expires > Date.now())) {
          const pctAtTarget = (Number(dollarValue) / (requiredSize as number)) * 100;
          setOverrides((prev) => ({ ...prev, [allocationId]: pctAtTarget }));
        }
      }
    } catch {}

    try { sessionStorage.setItem('portfolio-overflow-autosizing', String(Date.now() + 6000)); } catch {}

    const ok = await updatePortfolioSize(requiredSize as number);
    if (ok && calcMode === 'dollar') {
      try {
        const pending = sessionStorage.getItem('portfolio-pending-oversize');
        if (!pending) {
          setOverrides({});
        }
      } catch {
        setOverrides({});
      }
    }
  }, [calcMode, currentSize, updatePortfolioSize, setOverrides, computedTotals, persistedTotalsDollar, fallbackTotals]);
}
